import React, { useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import "../components/css/style.css";
import growthIllustration from "../assets/imgs/Rhythm_Jain_Photo.png";
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

export default function HeroSection() {
  const controls = useAnimation();
  const sectionRef = useRef(null);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const statsVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
        delay: 0.8,
      },
    },
  };

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
    },
  };

  const scrollVariants = {
    hidden: { 
      opacity: 0,
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.8
      }
    }
  };

  const statScrollVariants = {
    hidden: { 
      opacity: 0,
      x: -20
    },
    visible: (custom) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: custom * 0.2,
        type: "spring",
        stiffness: 100
      }
    })
  };

  // Stats data
  const statsData = [
    { value: 2, suffix: "+", label: "Years Experience", prefix: "" },
    { value: 100, suffix: "+", label: "Happy Clients", prefix: "" }
  ];

  const [statsRef, statsInView] = useInView({
    triggerOnce: false, 
    threshold: 0.3,
  });

  const [heroRef, heroInView] = useInView({
    triggerOnce: false, 
    threshold: 0.1,
  });

  useEffect(() => {
    if (heroInView) {
      controls.start("visible");
    } else { }
  }, [heroInView, controls]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (heroInView) {
        controls.start("visible");
      }
    }, 100);
    
    return () => clearTimeout(timeout);
  }, [controls, heroInView]);

  return (
    <section className="hero" ref={sectionRef}>
      <div className="hero-background">
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
        <div className="grid-pattern" />
      </div>

      <motion.div 
        ref={heroRef}
        className="hero-container"
        variants={containerVariants}
        initial="hidden"
        animate={controls} 
        viewport={{ 
          once: false,
          amount: 0.3,
          margin: "-50px"
        }}
      >
        <div className="hero-left">
          <motion.h1
            variants={itemVariants}
            className="hero-title"
          >
            GROW YOUR <span className="highlight">BUSINESS</span> WITH US
            <motion.span 
              className="title-accent"
              initial={{ width: 0 }}
              animate={heroInView ? { width: "100%" } : { width: 0 }} 
              transition={{ delay: 1, duration: 0.8 }}
            />
          </motion.h1>

          <motion.p 
            variants={itemVariants}
            className="hero-subtitle cocogogo-font"
          >
            At RhythmGrowthLab, we combine smart technology with data-driven
            marketing to deliver real business growth. From custom websites and
            mobile apps to SEO, performance marketing, and brand identity, we
            build digital systems designed to convert and scale.
          </motion.p>

          <motion.p 
            variants={itemVariants}
            className="hero-subtitle cocogogo-font highlight-text"
          >
            <TrendingUp size={16} className="inline-icon" />
            We don't just create digital products — we create growth engines
            that help ambitious businesses launch faster, perform better, and
            dominate their market.
          </motion.p>

          <motion.div 
            variants={itemVariants}
            className="hero-cta-group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <button className="hero-button primary" onClick={() => window.location.href = "/contact-us"}>
              <span>Contact Us Now </span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight size={18} />
              </motion.div>
            </button>
          </motion.div>

          <motion.div 
            ref={statsRef}
            className="hero-stats"
            variants={statsVariants}
            animate={heroInView ? "visible" : "hidden"} 
            viewport={{ 
              once: false, 
              amount: 0.3,
              margin: "-20px"
            }}
          >
            {statsData.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-card"
                custom={index}
                variants={statScrollVariants}
                whileHover={{ y: -5, scale: 1.05 }}
                animate={heroInView ? "visible" : "hidden"} 
                transition={{ type: "spring", stiffness: 300 }}
              >
                <motion.h3
                  initial={{ scale: 0 }}
                  animate={heroInView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 1 + index * 0.2, type: "spring" }}
                >
                  {statsInView ? (
                    <CountUp
                      start={0}
                      end={stat.value}
                      duration={2.5}
                      delay={0.5 + index * 0.2}
                      suffix={stat.suffix}
                      prefix={stat.prefix}
                      separator=","
                      useEasing={true}
                      preserveValue={true} 
                    />
                  ) : (
                    `${stat.value}${stat.suffix}`
                  )}
                </motion.h3>
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.div
          variants={itemVariants}
          className="hero-right"
          animate={heroInView ? "visible" : "hidden"} 
          viewport={{ 
            once: false, 
            amount: 0.3,
            margin: "-30px"
          }}
        >
          <div className="illustration-wrapper image-wrapper">
            <motion.div 
              className="glow"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            
            <motion.div 
              className="floating-elements"
              animate={floatingAnimation}
            >
              <div className="floating-dot dot-1" />
              <div className="floating-dot dot-2" />
              <div className="floating-dot dot-3" />
            </motion.div>

            <motion.div
              variants={scrollVariants}
              animate={heroInView ? "visible" : "hidden"} 
            >
              <motion.img
                src={growthIllustration}
                alt="RhythmGrowthlab Creative Illustration"
                className="hero-image"
                whileHover={{ 
                  scale: 1.05,
                  rotate: 2,
                  transition: { type: "spring", stiffness: 300 }
                }}
              />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}